import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import ResultsModal from '../components/ResultsModal';
import WarnOnce from '../components/WarnOnce';
import { errorMessage } from '../utils/errors';
import { formatCategories, levelLabel, registrationMode } from '../components/TournamentCard';

const WARN_TEAM_MSG = "Il ne s'agit pas d'une inscription officielle mais d'un moyen de centraliser les informations et de vous organiser plus facilement. Pensez à vous inscrire sur les plateformes officielles.";

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const GENDER_LABELS  = { mix: 'Mixte', masculin: 'Masculin', feminin: 'Féminin' };
const LEVEL_LABELS   = { loisir: 'Loisir', departemental: 'Départ.', regional: 'Régional', national: 'National', pro: 'Pro' };

function TonRetourSection() {
  const [review, setReview] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [rating, setRating] = React.useState(0);
  if (submitted) {
    return (
      <div>
        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Ton retour</div>
        <div className="card" style={{ padding: 14, background: 'rgba(107,170,30,0.06)', borderColor: 'rgba(107,170,30,0.3)' }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
            {[1,2,3,4,5].map(i => (
              <span key={i} style={{ fontSize: 16, color: i <= rating ? 'var(--sun)' : 'var(--ink-3)' }}>★</span>
            ))}
          </div>
          <p style={{ fontSize: 13, color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>{review}</p>
          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 8 }}>Partagé dans le fil</div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 10 }}>Ton retour</div>
      <div className="card col" style={{ gap: 10, padding: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600 }}>Partage ton expérience</div>
        <div style={{ display: 'flex', gap: 6 }}>
          {[1,2,3,4,5].map(i => (
            <button key={i} type="button" onClick={() => setRating(i)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 22, color: i <= rating ? 'var(--sun)' : 'var(--ink-3)' }}>★</button>
          ))}
        </div>
        <textarea value={review} onChange={e => setReview(e.target.value)}
          placeholder="Belle organisation, ambiance au top…"
          style={{ width: '100%', minHeight: 72, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--line)',
            background: 'var(--ink-2)', color: 'var(--text)', fontSize: 13, lineHeight: 1.5, resize: 'none', boxSizing: 'border-box' }} />
        <button type="button" className="btn btn-primary btn-sm"
          disabled={!review.trim() || rating === 0}
          onClick={() => setSubmitted(true)}>
          Partager
        </button>
      </div>
    </div>
  );
}
const SURFACE_LABELS = { green: '🌿 Green', beach: '🏖️ Beach', gymnase: '🏛️ Gymnase', neige: '❄️ Neige' };

const AV_COLORS = ['av-blue','av-pink','av-green','av-orange','av-purple','av-teal','av-red','av-indigo'];
function avatarColor(id) {
  let h = 0; for (const c of String(id)) h = (h * 31 + c.charCodeAt(0)) & 0xFFFFFF;
  return AV_COLORS[Math.abs(h) % AV_COLORS.length];
}
function initials(firstName, lastName) {
  const f = (firstName || '').trim(); const l = (lastName || '').trim();
  if (f && l) return (f[0] + l[0]).toUpperCase();
  return f ? f.slice(0, 2).toUpperCase() : '?';
}
function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  return `${parseInt(h, 10)}h${m}`;
}

function getJoinBlockReason(user, tournament, team) {
  const totalOccupied = team.members.length + (team.externalMembers?.length || 0);
  if (!user.gender) return 'Genre non défini sur votre profil';
  if (totalOccupied >= team.maxSize) return 'Équipe complète';
  if (tournament.gender === 'masculin' && user.gender !== 'masculin') return 'Tournoi masculin uniquement';
  if (tournament.gender === 'feminin'  && user.gender !== 'feminin')  return 'Tournoi féminin uniquement';
  if (tournament.gender === 'mix') {
    const newSize = team.members.length + 1;
    if (newSize === team.maxSize) {
      const hasFemale = team.memberDetails.some(m => m.gender === 'feminin') || user.gender === 'feminin';
      if (!hasFemale) return 'Dernière place réservée à une femme (équipe mixte)';
    }
  }
  return null;
}


function shortLocation(location) {
  if (!location) return '';
  const parts = location.split(', ');
  if (parts.length <= 2) return location;
  return parts[0] + ' · ' + parts[parts.length - 1];
}


function calDate(dateStr, timeStr) {
  const [y, m, d] = dateStr.split('-');
  const [h, min]  = (timeStr || '10:00').split(':');
  return `${y}${m}${d}T${h.padStart(2,'0')}${(min||'00').padStart(2,'0')}00`;
}
function googleCalUrl(t) {
  const start = calDate(t.date, t.time);
  const endMs  = new Date(`${t.date}T${t.time||'10:00'}:00`).getTime() + 8*3600000;
  const endD   = new Date(endMs);
  const end    = calDate(endD.toISOString().split('T')[0], endD.toTimeString().slice(0,5));
  const p = new URLSearchParams({
    action: 'TEMPLATE', text: t.name,
    dates: `${start}/${end}`,
    location: t.location || '',
    details: [t.playerFormat, GENDER_LABELS[t.gender], SURFACE_LABELS[t.surface]].filter(Boolean).join(' · ')
  });
  return `https://calendar.google.com/calendar/render?${p}`;
}
function buildICS(t) {
  const start = calDate(t.date, t.time);
  const endMs  = new Date(`${t.date}T${t.time||'10:00'}:00`).getTime() + 8*3600000;
  const endD   = new Date(endMs);
  const end    = calDate(endD.toISOString().split('T')[0], endD.toTimeString().slice(0,5));
  return ['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//Tournoi Volley//FR',
    'BEGIN:VEVENT',`DTSTART:${start}`,`DTEND:${end}`,
    `SUMMARY:${t.name}`,`LOCATION:${(t.location||'').replace(/\n/g,'\\n')}`,
    `DESCRIPTION:${[t.playerFormat,GENDER_LABELS[t.gender]].filter(Boolean).join(' - ')}`,
    'END:VEVENT','END:VCALENDAR'].join('\r\n');
}
function downloadICS(t) {
  const blob = new Blob([buildICS(t)], { type: 'text/calendar' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `${t.name.replace(/\s+/g,'-')}.ics`;
  a.click(); URL.revokeObjectURL(a.href);
}

function computeCountdown(dateStr, timeStr) {
  const target = new Date(`${dateStr}T${(timeStr || '10:00').padStart(5, '0')}:00`);
  const diff = target - new Date();
  if (diff <= 0) return { dd: '00', hh: '00', mm: '00', past: true };
  const totalMins = Math.floor(diff / 60000);
  const dd = Math.floor(totalMins / 1440);
  const hh = Math.floor((totalMins % 1440) / 60);
  const mm = totalMins % 60;
  return { dd: String(dd).padStart(2, '0'), hh: String(hh).padStart(2, '0'), mm: String(mm).padStart(2, '0'), past: false };
}

function surfaceToPhoto(surface) {
  if (surface === 'beach') return 'photo-beach';
  if (surface === 'green') return 'photo-sunset';
  if (surface === 'gymnase') return 'photo-court';
  return 'photo-beach';
}

function splitName(name) {
  if (!name) return ['', ''];
  const words = name.trim().split(' ');
  if (words.length <= 2) return [name, ''];
  const mid = Math.ceil(words.length / 2);
  return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
}

function cityFromLocation(location) {
  if (!location) return '';
  const parts = location.split(', ');
  return parts[parts.length - 1] || parts[0];
}

function LocationRow({ tournament }) {
  const [open, setOpen] = useState(false);
  const addr = tournament.location || '';
  const googleUrl = tournament.lat && tournament.lng
    ? `https://maps.google.com/?q=${tournament.lat},${tournament.lng}`
    : `https://maps.google.com/?q=${encodeURIComponent(addr)}`;
  const wazeUrl = tournament.lat && tournament.lng
    ? `https://waze.com/ul?ll=${tournament.lat},${tournament.lng}&navigate=yes`
    : `https://waze.com/ul?q=${encodeURIComponent(addr)}&navigate=yes`;
  const copyAddr = () => { navigator.clipboard.writeText(addr); setOpen(false); };

  return (
    <>
      <div className="card row" style={{ padding: 14, gap: 12, marginBottom: 8, cursor: 'pointer' }}
        onClick={() => setOpen(true)}>
        <div style={{ width: 38, height: 38, borderRadius: 12, background: 'rgba(10,143,168,0.14)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z"/>
            <circle cx="12" cy="9" r="2.5"/>
          </svg>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="mono" style={{ color: 'var(--text-3)', marginBottom: 2 }}>Lieu</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)' }}>{shortLocation(tournament.location)}</div>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      </div>
      {open && (
        <div className="bottom-sheet-overlay" onClick={() => setOpen(false)}>
          <div className="bottom-sheet" onClick={e => e.stopPropagation()}>
            <div className="bottom-sheet-handle" />
            <div style={{ padding: '0 20px 20px' }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Se rendre sur place</div>
              <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 20 }}>{addr}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <a href={googleUrl} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    borderRadius: 'var(--r-md)', background: 'var(--ink-3)', textDecoration: 'none' }}
                  onClick={() => setOpen(false)}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🗺️</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Google Maps</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Ouvrir dans Google Maps</div>
                  </div>
                </a>
                <a href={wazeUrl} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    borderRadius: 'var(--r-md)', background: 'var(--ink-3)', textDecoration: 'none' }}
                  onClick={() => setOpen(false)}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🚗</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Waze</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Ouvrir dans Waze</div>
                  </div>
                </a>
                <button onClick={copyAddr}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                    borderRadius: 'var(--r-md)', background: 'var(--ink-3)', border: 'none',
                    cursor: 'pointer', width: '100%', textAlign: 'left' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>📋</div>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>Copier l'adresse</div>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Coller dans votre appli préférée</div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const WMO_CODES = [
  [0,   'Ciel dégagé',          '☀️'],
  [1,   'Peu nuageux',           '🌤️'],
  [2,   'Partiellement nuageux', '⛅'],
  [3,   'Couvert',               '☁️'],
  [45,  'Brouillard',            '🌫️'],
  [48,  'Brouillard givrant',    '🌫️'],
  [51,  'Bruine légère',         '🌦️'],
  [53,  'Bruine modérée',        '🌦️'],
  [55,  'Bruine forte',          '🌧️'],
  [61,  'Pluie légère',          '🌧️'],
  [63,  'Pluie modérée',         '🌧️'],
  [65,  'Pluie forte',           '🌧️'],
  [71,  'Neige légère',          '❄️'],
  [73,  'Neige modérée',         '❄️'],
  [75,  'Neige forte',           '❄️'],
  [80,  'Averses légères',       '🌦️'],
  [81,  'Averses modérées',      '🌦️'],
  [82,  'Averses fortes',        '🌧️'],
  [95,  'Orage',                 '⛈️'],
  [96,  'Orage avec grêle',      '⛈️'],
  [99,  'Orage fort avec grêle', '🌩️'],
];
function wmoInfo(code) {
  for (let i = WMO_CODES.length - 1; i >= 0; i--) {
    if (code >= WMO_CODES[i][0]) return { label: WMO_CODES[i][1], icon: WMO_CODES[i][2] };
  }
  return { label: 'Conditions inconnues', icon: '🌡️' };
}

function WeatherCard({ tournament }) {
  const [weather, setWeather] = useState(null);
  const [status, setStatus]   = useState('loading'); // 'loading' | 'ok' | 'error' | 'unavailable'

  useEffect(() => {
    if (!tournament.date) { setStatus('unavailable'); return; }
    let cancelled = false;
    (async () => {
      try {
        let lat = tournament.lat ? parseFloat(tournament.lat) : null;
        let lng = tournament.lng ? parseFloat(tournament.lng) : null;
        if (!lat || !lng) {
          const geoQuery = tournament.city || tournament.location || '';
          const geoRes  = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(geoQuery)}&format=json&limit=1`,
            { headers: { 'Accept-Language': 'fr' } }
          );
          const geoData = await geoRes.json();
          if (!geoData.length) { if (!cancelled) setStatus('unavailable'); return; }
          lat = parseFloat(geoData[0].lat);
          lng = parseFloat(geoData[0].lon);
        }
        const res  = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
          `&daily=temperature_2m_max,temperature_2m_min,precipitation_probability_max,windspeed_10m_max,weathercode` +
          `&timezone=auto&forecast_days=16`
        );
        const data = await res.json();
        const idx  = (data.daily?.time || []).indexOf(tournament.date);
        if (idx === -1) { if (!cancelled) setStatus('unavailable'); return; }
        if (!cancelled) {
          setWeather({
            tempMax: data.daily.temperature_2m_max[idx],
            tempMin: data.daily.temperature_2m_min[idx],
            precip:  data.daily.precipitation_probability_max[idx],
            wind:    data.daily.windspeed_10m_max[idx],
            code:    data.daily.weathercode[idx],
          });
          setStatus('ok');
        }
      } catch { if (!cancelled) setStatus('error'); }
    })();
    return () => { cancelled = true; };
  }, [tournament.date, tournament.lat, tournament.lng, tournament.city, tournament.location]);

  const iconBg = 'rgba(245,166,35,0.18)';
  const sunIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--sun-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/>
      <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
      <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  );

  if (status === 'loading') return (
    <div className="card row" style={{ padding: 14, gap: 12, marginBottom: 8 }}>
      <div style={{ width: 38, height: 38, borderRadius: 12, background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {sunIcon}
      </div>
      <div>
        <div className="mono" style={{ color: 'var(--text-3)', marginBottom: 2 }}>Météo</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Chargement…</div>
      </div>
    </div>
  );

  if (status !== 'ok' || !weather) return (
    <div className="card row" style={{ padding: 14, gap: 12, marginBottom: 8 }}>
      <div style={{ width: 38, height: 38, borderRadius: 12, background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {sunIcon}
      </div>
      <div>
        <div className="mono" style={{ color: 'var(--text-3)', marginBottom: 2 }}>Météo</div>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Prévisions non disponibles</div>
      </div>
    </div>
  );

  const { label, icon } = wmoInfo(weather.code);
  return (
    <div className="card" style={{ padding: 14, marginBottom: 8 }}>
      <div className="mono" style={{ color: 'var(--text-3)', marginBottom: 10 }}>Météo prévue</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 48, height: 48, borderRadius: 14, background: iconBg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{label}</div>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
              {Math.round(weather.tempMax)}°
              <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--text-3)' }}> / {Math.round(weather.tempMin)}°</span>
            </span>
            {weather.precip != null && (
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>💧 {weather.precip}%</span>
            )}
            {weather.wind != null && (
              <span style={{ fontSize: 12, color: 'var(--text-3)' }}>💨 {Math.round(weather.wind)} km/h</span>
            )}
          </div>
        </div>
      </div>
      <div className="mono" style={{ fontSize: 9, color: 'var(--text-3)', marginTop: 10, letterSpacing: '0.05em' }}>
        SOURCE · OPEN-METEO · PRÉVISION À 16J
      </div>
    </div>
  );
}

function InfoRow({ label, value, sub, iconBg, icon }) {
  return (
    <div className="card row" style={{ padding: 14, gap: 12, marginBottom: 8 }}>
      <div style={{ width: 38, height: 38, borderRadius: 12, background: iconBg || 'var(--ink-3)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="mono" style={{ color: 'var(--text-3)', marginBottom: 2 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{value}</div>
        {sub && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function TournamentDetail({ user }) {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');

  const [tournament, setTournament]   = useState(null);
  const [teams, setTeams]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [newTeamName, setNewTeamName] = useState('');
  const [newTeamExternals, setNewTeamExternals] = useState([]);
  const [newTeamVisibility, setNewTeamVisibility] = useState('open');
  const [newTeamFormat, setNewTeamFormat] = useState('');
  const [newTeamGender, setNewTeamGender] = useState('');
  const [newTeamCategoryId, setNewTeamCategoryId] = useState('');
  const [warning, setWarning] = useState('');
  const [pendingMembers, setPendingMembers] = useState([]); // users to add after creation
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [editingTeamId, setEditingTeamId]   = useState(null); // null = création, string = édition
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQ, setSearchQ]           = useState('');
  const [levelFilters, setLevelFilters] = useState(new Set());
  const [filtersOpen, setFiltersOpen]   = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [resultsModalTeam, setResultsModalTeam] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const t = searchParams.get('tab');
    return ['infos','equipes','resultats'].includes(t) ? t : 'infos';
  });
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [myPayment, setMyPayment] = useState(null); // { paid, amount, date } | null (pas encore chargé)
  const [showClaim, setShowClaim] = useState(false);
  const [claimEmail, setClaimEmail] = useState('');
  const [claimMsg, setClaimMsg] = useState('');
  const [claimLoading, setClaimLoading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [tRes, teamsRes] = await Promise.all([
        axios.get(`${API_URL}/api/tournaments/${tournamentId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/tournaments/${tournamentId}/teams`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setTournament(tRes.data);
      setTeams(teamsRes.data);
    } catch (err) {
      setError(errorMessage(err, "Impossible de charger ce tournoi. Vérifie ta connexion puis réessaie."));
    } finally {
      setLoading(false);
    }
  }, [tournamentId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fetch group member suggestions when create team form opens
  useEffect(() => {
    if (!showCreateTeam) return;
    axios.get(`${API_URL}/api/groups`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        const seen = new Set([user.id]);
        const suggs = [];
        for (const g of (res.data || [])) {
          for (const m of (g.memberDetails || [])) {
            if (!seen.has(m.id)) { seen.add(m.id); suggs.push(m); }
          }
        }
        setGroupSuggestions(suggs.slice(0, 6));
      })
      .catch(() => {});
  }, [showCreateTeam]); // eslint-disable-line

  // Statut de paiement HelloAsso (une fois inscrit) — reconsulté au retour dans l'app (focus)
  // pour que le badge bascule sans quitter l'écran après le paiement fait sur HelloAsso.
  const fetchMyPayment = useCallback(() => {
    axios.get(`${API_URL}/api/tournaments/${tournamentId}/my-payment`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setMyPayment(res.data))
      .catch(() => {});
  }, [tournamentId, token]);

  useEffect(() => {
    if (!tournament || registrationMode(tournament) !== 'helloasso' || !teams.some(t => t.members.includes(user.id))) return;
    fetchMyPayment();
    window.addEventListener('focus', fetchMyPayment);
    return () => window.removeEventListener('focus', fetchMyPayment);
  }, [tournament, teams, fetchMyPayment]); // eslint-disable-line

  const handleDeclarePayment = async (declared) => {
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/payments/declare`,
        { declared }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) { setError(errorMessage(err)); }
  };

  const handleClaimPayment = async (e) => {
    e.preventDefault();
    setClaimLoading(true); setClaimMsg('');
    try {
      const res = await axios.post(`${API_URL}/api/tournaments/${tournamentId}/payments/claim`,
        { email: claimEmail }, { headers: { Authorization: `Bearer ${token}` } });
      setClaimMsg(res.data.message);
    } catch (err) {
      setClaimMsg(errorMessage(err));
    } finally { setClaimLoading(false); }
  };

  const { containerRef: pageRef, onTouchStart: ptrStart, onTouchEnd: ptrEnd, refreshing: ptrRefreshing } = usePullToRefresh(fetchData);

  const handleCreateTeam = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setActionLoading(true); setError(''); setWarning('');
    try {
      const categories = tournament.categories;
      let body;
      if (Array.isArray(categories) && categories.length > 0) {
        const categoryId = categories.length === 1 ? categories[0].id : newTeamCategoryId;
        body = { name: newTeamName, externalMembers: newTeamExternals, visibility: newTeamVisibility, categoryId };
      } else {
        const availableFormats = tournament.playerFormats || (tournament.playerFormat ? [tournament.playerFormat] : []);
        const availableGenders = tournament.genders || (tournament.gender ? [tournament.gender] : []);
        const teamFormat = availableFormats.length === 1 ? availableFormats[0] : newTeamFormat;
        const teamGender = availableGenders.length === 1 ? availableGenders[0] : newTeamGender;
        body = { name: newTeamName, externalMembers: newTeamExternals, visibility: newTeamVisibility,
          playerFormat: teamFormat, gender: teamGender };
      }
      const res = await axios.post(`${API_URL}/api/tournaments/${tournamentId}/teams`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      for (const m of pendingMembers) {
        try {
          await axios.post(`${API_URL}/api/tournaments/${tournamentId}/teams/${res.data.teamId}/add-member`,
            { userId: m.id }, { headers: { Authorization: `Bearer ${token}` } });
        } catch { /* ignore individual errors */ }
      }
      setNewTeamName(''); setNewTeamExternals([]); setNewTeamVisibility('open');
      setNewTeamFormat(''); setNewTeamGender(''); setNewTeamCategoryId('');
      setPendingMembers([]); setShowCreateTeam(false); setWarning(res.data.warning || '');
      fetchData();
    } catch (err) {
      setError(errorMessage(err, "La création de l'équipe a échoué. Réessaie dans un instant."));
    } finally { setActionLoading(false); }
  };

  const handleOpenChat = async (team) => {
    if (team.conversationId) { navigate(`/messages/${team.conversationId}`); return; }
    setActionLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/tournaments/${tournamentId}/teams/${team.id}/conversation`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      navigate(`/messages/${res.data.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setActionLoading(false);
    }
  };

  const handleJoinTeam = async (teamId, externalMembers = []) => {
    setActionLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/teams/${teamId}/join`,
        { externalMembers },
        { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      setError(errorMessage(err));
    } finally { setActionLoading(false); }
  };

  const handleRequestJoin = async (teamId) => {
    setActionLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/teams/${teamId}/request-join`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      setError(errorMessage(err));
    } finally { setActionLoading(false); }
  };

  const handleLeaveTeam = async (teamId) => {
    setActionLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/api/tournaments/${tournamentId}/teams/${teamId}/leave`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      setError(errorMessage(err));
    } finally { setActionLoading(false); }
  };

  const handleDeleteTeam = async (team) => {
    if (!window.confirm(`Supprimer l'équipe "${team.name}" ?`)) return;
    setActionLoading(true); setError('');
    try {
      await axios.delete(`${API_URL}/api/tournaments/${tournamentId}/teams/${team.id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      setError(errorMessage(err));
    } finally { setActionLoading(false); }
  };

  const openEditTeam = (team) => {
    setNewTeamName(team.name);
    setNewTeamVisibility(team.visibility || 'open');
    setNewTeamFormat(team.playerFormat || '');
    setNewTeamGender(team.gender || '');
    setNewTeamCategoryId(team.categoryId || '');
    setNewTeamExternals(team.externalMembers || []);
    // Précharge les membres inscrits réels (hors soi, affiché comme capitaine) pour un roster correct.
    setPendingMembers((team.memberDetails || []).filter(m => m.id !== user.id));
    setEditingTeamId(team.id);
    setShowCreateTeam(true);
  };

  const handleUpdateTeam = async () => {
    if (!newTeamName.trim() || !editingTeamId) return;
    setActionLoading(true); setError(''); setWarning('');
    try {
      const categories = tournament.categories;
      const members = [user.id, ...pendingMembers.map(m => m.id)];
      const memberDetails = [
        { id: user.id, firstName: user.firstName, lastName: user.lastName, gender: user.gender, level: user.level, avatarUrl: user.avatarUrl || null },
        ...pendingMembers.map(m => ({ id: m.id, firstName: m.firstName, lastName: m.lastName, gender: m.gender, level: m.level, avatarUrl: m.avatarUrl || null })),
      ];
      const body = { name: newTeamName.trim(), visibility: newTeamVisibility,
        externalMembers: newTeamExternals, members, memberDetails };
      if (Array.isArray(categories) && categories.length > 0) {
        body.categoryId = categories.length === 1 ? categories[0].id : newTeamCategoryId;
      } else {
        const availableFormats = tournament.playerFormats || (tournament.playerFormat ? [tournament.playerFormat] : []);
        const availableGenders = tournament.genders || (tournament.gender ? [tournament.gender] : []);
        body.playerFormat = availableFormats.length === 1 ? availableFormats[0] : newTeamFormat;
        body.gender = availableGenders.length === 1 ? availableGenders[0] : newTeamGender;
      }
      const res = await axios.put(`${API_URL}/api/tournaments/${tournamentId}/teams/${editingTeamId}`, body,
        { headers: { Authorization: `Bearer ${token}` } });
      setShowCreateTeam(false); setEditingTeamId(null);
      setNewTeamName(''); setNewTeamExternals([]); setNewTeamVisibility('open');
      setNewTeamFormat(''); setNewTeamGender(''); setNewTeamCategoryId(''); setPendingMembers([]);
      setWarning(res.data.warning || '');
      fetchData();
    } catch (err) {
      setError(errorMessage(err));
    } finally { setActionLoading(false); }
  };


  const handleRemoveExternal = async (teamId, extId) => {
    setActionLoading(true);
    try {
      await axios.delete(`${API_URL}/api/tournaments/${tournamentId}/teams/${teamId}/external/${extId}`,
        { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (err) {
      setError(errorMessage(err));
    } finally { setActionLoading(false); }
  };

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: 'var(--ink)' }}>
      <div style={{ height: 320, background: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 13, color: 'var(--text-3)' }}>Chargement…</div>
      </div>
    </div>
  );
  if (!tournament) return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14, minHeight: 0, background: 'var(--ink)', padding: 24, textAlign: 'center' }}>
      <div style={{ fontSize: 40 }}>🏐</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', maxWidth: 320 }}>
        {error || "Ce tournoi est introuvable. Il a peut-être été supprimé."}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={fetchData} className="btn" style={{ background: 'var(--sun)', color: 'var(--ink)', border: 'none', borderRadius: 999, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' }}>Réessayer</button>
        <button onClick={() => navigate(-1)} className="btn" style={{ background: 'var(--ink-3)', color: 'var(--text)', border: 'none', borderRadius: 999, padding: '10px 18px', fontWeight: 700, cursor: 'pointer' }}>Retour</button>
      </div>
    </div>
  );

  const myTeam = teams.find(t => t.members.includes(user.id));
  const isCreator = tournament.creator === user.id || user?.isAdmin === true;
  const _td = new Date();
  const isPast = tournament.date < `${_td.getFullYear()}-${String(_td.getMonth()+1).padStart(2,'0')}-${String(_td.getDate()).padStart(2,'0')}`;

  const { dd, hh, mm } = computeCountdown(tournament.date, tournament.time);
  const photoClass = surfaceToPhoto(tournament.surface);
  const [nameLine1, nameLine2] = splitName(tournament.name);
  const cityName = cityFromLocation(tournament.location);

  const dateStr = tournament.date
    ? new Date(tournament.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
    : '';
  const shortDate = tournament.date
    ? (() => {
        const s = new Date(tournament.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
        return s.charAt(0).toUpperCase() + s.slice(1);
      })()
    : '';

  const LEVEL_KEYS  = ['loisir','departemental','regional','national','pro'];
  const LEVEL_MAP   = { loisir:'Loisir', departemental:'Départemental', regional:'Régional', national:'National', pro:'Pro' };
  const LEVEL_CHIPS = ['Loisir','Départ.','Régional','National','Pro'];

  const toggleLevel = (lv) => setLevelFilters(prev => {
    const next = new Set(prev);
    if (next.has(lv)) next.delete(lv); else next.add(lv);
    return next;
  });

  const filteredTeams = teams.filter(t => {
    const q = searchQ.toLowerCase();
    const nameMatch = !searchQ ||
      t.name.toLowerCase().includes(q) ||
      (t.memberDetails || []).some(m => `${m.firstName} ${m.lastName}`.toLowerCase().includes(q));
    const levelMatch = levelFilters.size === 0 || Array.from(levelFilters).some(
      lv => (t.averageLevelLabel || '').toLowerCase() === (LEVEL_MAP[lv] || '').toLowerCase()
    );
    return nameMatch && levelMatch;
  });

  const myFilteredTeam = filteredTeams.find(t => t.members.includes(user.id));
  const otherTeams = filteredTeams.filter(t => !t.members.includes(user.id));


  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0, background: 'var(--ink)', position: 'relative' }}>

      {/* ── HERO ── */}
      <div className="tourney-hero sand-grain on-hero" style={{ padding: '4px 20px 16px', flexShrink: 0 }}>
        <div className={`hero-photo ${photoClass}`} />
        <div className="hero-tint" />

        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8 }}>
          <button aria-label="Retour" onClick={() => navigate(-1)} style={{ width: 40, height: 40, borderRadius: '50%',
            background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
          {isCreator && (
            <button aria-label="Modifier le tournoi" onClick={() => navigate(`/tournaments/${tournamentId}/edit`)} style={{ width: 40, height: 40, borderRadius: '50%',
              background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', border: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
              </svg>
            </button>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          {isPast && <span className="chip" style={{ color: 'rgba(255,255,255,0.7)', background: 'rgba(255,255,255,0.12)', borderColor: 'rgba(255,255,255,0.2)', marginBottom: 6, display: 'inline-block' }}>Passé</span>}
          <div className="display" style={{ fontSize: 28, lineHeight: 1.05 }}>{nameLine1}</div>
          {nameLine2 && <div className="display" style={{ fontSize: 28, color: '#FFD37A', lineHeight: 1.05 }}>{nameLine2}</div>}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10 }}>
            <span className="display" style={{ fontSize: 17, color: 'rgba(255,255,255,0.95)' }}>{cityName}</span>
            <span style={{ color: 'rgba(255,255,255,0.4)' }}>·</span>
            <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>{dateStr}{tournament.time ? ` · ${tournament.time}` : ''}</span>
          </div>
        </div>

        {!isPast ? (
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {[{ v: dd, l: 'jours' }, { v: hh, l: 'h' }, { v: mm, l: 'min' }].map(c => (
              <div key={c.l} style={{ flex: 1, padding: '8px 0', textAlign: 'center',
                borderRadius: 'var(--r-md)', background: 'rgba(255,255,255,0.16)',
                backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.22)' }}>
                <div className="display" style={{ fontSize: 24, color: '#FFD37A' }}>{c.v}</div>
                <div className="mono" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 9 }}>{c.l}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ marginTop: 12, padding: '8px 14px', textAlign: 'center',
            borderRadius: 'var(--r-md)', background: 'rgba(255,255,255,0.12)',
            border: '1px solid rgba(255,255,255,0.2)' }}>
            <span className="mono" style={{ color: 'rgba(255,255,255,0.65)' }}>Tournoi terminé</span>
          </div>
        )}
      </div>

      {/* ── 3 tabs ── */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--line)', flexShrink: 0, background: 'var(--ink)' }}>
        {[['infos', 'Infos'], ['equipes', 'Équipes'], ['resultats', 'Résultats']].map(([id, lbl]) => (
          <button key={id} onClick={() => setActiveTab(id)} style={{
            flex: 1, padding: '12px 0', position: 'relative',
            fontSize: 13, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer',
            color: activeTab === id ? 'var(--text)' : 'var(--text-3)',
          }}>
            {lbl}
            {activeTab === id && (
              <span style={{ position: 'absolute', bottom: 0, left: '25%', right: '25%',
                height: 2, background: 'var(--sun)', borderRadius: 2, display: 'block' }} />
            )}
          </button>
        ))}
      </div>

      {/* ── Scrollable content ── */}
      <div className="page-content" ref={pageRef} onTouchStart={ptrStart} onTouchEnd={ptrEnd}
        style={{ paddingBottom: 160 }}>
        {ptrRefreshing && (
          <div className="ptr-indicator"><div className="ptr-spinner" /><span>Actualisation…</span></div>
        )}
        {error && <div className="message error" style={{ margin: '12px 16px 0' }}>{error}</div>}
        {warning && <div className="message info" style={{ margin: '12px 16px 0' }}>{warning}</div>}

        {/* ── INFOS tab ── */}
        {activeTab === 'infos' && (
          <div style={{ padding: '16px 16px 0' }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.09em', color: 'var(--text-3)', marginBottom: 10 }}>
              Infos pratiques
            </div>
            <LocationRow tournament={tournament} />
            {(tournament.date || tournament.time) && (
              <InfoRow
                label="Horaire"
                value={[shortDate, tournament.time ? `· ${formatTime(tournament.time)}` : ''].filter(Boolean).join(' ')}
                sub={tournament.meetingTime ? `Fermeture des inscriptions à ${formatTime(tournament.meetingTime)}` : undefined}
                iconBg="rgba(10,143,168,0.14)"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--cyan)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>}
              />
            )}
            {tournament.surface && (
              <InfoRow
                label="Surface"
                value={SURFACE_LABELS[tournament.surface] || tournament.surface}
                iconBg="rgba(245,166,35,0.18)"
                icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--sun-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>}
              />
            )}
            <InfoRow
              label="Prix"
              value={tournament.price > 0 ? `${tournament.price} € / ${tournament.priceMode === 'team' ? 'équipe' : 'joueur'}` : 'Gratuit'}
              sub={registrationMode(tournament) === 'spike' && tournament.price > 0 ? 'à régler sur place' : undefined}
              iconBg="rgba(107,170,30,0.14)"
              icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--lime)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>}
            />
            <div className="card" style={{ padding: 14, marginBottom: 8 }}>
              <div className="mono" style={{ color: 'var(--text-3)', marginBottom: 10 }}>Format & catégorie</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {formatCategories(tournament).map(c => (
                  <span key={c} className="chip chip-cyan">{c}</span>
                ))}
              </div>
            </div>
            <WeatherCard tournament={tournament} />
            {tournament.externalUrl && registrationMode(tournament) !== 'spike' && (
              <a href={tournament.externalUrl} target="_blank" rel="noreferrer"
                className="card" style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px',
                  marginBottom: 8, textDecoration: 'none', cursor: 'pointer',
                }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                  background: 'rgba(245,166,35,0.14)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--sun-2)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                  </svg>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 }}>
                    Inscription officielle
                  </div>
                  <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tournament.externalUrl}
                  </div>
                </div>
                <span style={{ padding: '3px 9px', borderRadius: 999, fontSize: 10, fontWeight: 700,
                  background: 'rgba(245,166,35,0.18)', color: 'var(--sun-2)', flexShrink: 0 }}>
                  OFFICIEL
                </span>
              </a>
            )}
            <div style={{ position: 'relative', marginBottom: 8 }}>
              <button onClick={() => setShowCalendar(v => !v)} className="btn btn-ghost btn-sm"
                style={{ width: '100%' }}>
                📅 Ajouter au calendrier
              </button>
              {showCalendar && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
                  background: 'var(--ink-2)', border: '1px solid var(--line)',
                  borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-card)', zIndex: 100, overflow: 'hidden' }}>
                  <a href={googleCalUrl(tournament)} target="_blank" rel="noreferrer"
                    style={{ display: 'block', padding: '10px 14px', fontSize: 13, color: 'var(--text)', textDecoration: 'none', borderBottom: '1px solid var(--line)' }}
                    onClick={() => setShowCalendar(false)}>
                    🗓 Google Calendar
                  </a>
                  <button style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                    fontSize: 13, color: 'var(--text)', background: 'none', border: 'none', cursor: 'pointer' }}
                    onClick={() => { downloadICS(tournament); setShowCalendar(false); }}>
                    📥 Apple / Outlook (.ics)
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── ÉQUIPES tab ── */}
        {activeTab === 'equipes' && (
          <div style={{ padding: '16px 16px 0' }}>
            {!user.gender && (
              <div className="message info">Genre non défini — vous ne pouvez pas rejoindre d'équipe.</div>
            )}

            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
              <div className="search-bar" style={{ flex: 1, margin: 0 }}>
                <span className="search-bar-icon">🔍</span>
                <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Équipe ou joueur…" />
                {searchQ && <button className="search-clear" onClick={() => setSearchQ('')}>✕</button>}
              </div>
              <button className={`filter-pill${levelFilters.size > 0 ? ' filter-pill-active' : ''}`}
                onClick={() => setFiltersOpen(v => !v)} style={{ flexShrink: 0 }}>
                🎚 Filtres
                {levelFilters.size > 0 && <span className="filter-pill-badge">{levelFilters.size}</span>}
              </button>
            </div>

            {filtersOpen && (
              <div className="filter-drawer-overlay" onClick={() => setFiltersOpen(false)}>
                <div className="filter-drawer" onClick={e => e.stopPropagation()}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', marginBottom: 12 }}>Niveau de jeu</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    <button className={`chip${levelFilters.size === 0 ? ' active' : ''}`}
                      onClick={() => { setLevelFilters(new Set()); setFiltersOpen(false); }}>
                      Tous
                    </button>
                    {LEVEL_KEYS.map((lv, i) => (
                      <button key={lv} className={`chip${levelFilters.has(lv) ? ' active' : ''}`}
                        onClick={() => toggleLevel(lv)}>
                        {LEVEL_CHIPS[i]}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {!myTeam && !showCreateTeam && user.gender && (
              <div style={{
                background: 'var(--ink-2)', border: '1.5px dashed var(--line)',
                borderRadius: 'var(--r-lg)', padding: '18px 16px', marginBottom: 14,
              }}>
                <div className="display" style={{ fontSize: 13, marginBottom: 6 }}>Pas encore d'équipe</div>
                <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 14, lineHeight: 1.4 }}>
                  Tu n'as pas encore d'équipe pour ce tournoi. Rejoins une équipe existante ou crée la tienne.
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => setShowCreateTeam(true)}
                  style={{ width: '100%' }}>
                  + Créer mon équipe
                </button>
              </div>
            )}

            {/* create-team form moved to full-screen overlay below */}

            {myTeam && registrationMode(tournament) === 'helloasso' && (
              <div className="card col gap-2" style={{ padding: 14, marginBottom: 14 }}>
                {myPayment?.paid ? (
                  <span className="chip" style={{ background: 'var(--lime)', color: 'var(--ink)', fontWeight: 700, alignSelf: 'flex-start' }}>
                    Inscription payée
                  </span>
                ) : (
                  <>
                    <button className="btn btn-primary btn-sm" style={{ width: '100%' }}
                      onClick={() => window.open(tournament.externalUrl, '_blank', 'noopener')}>
                      Payer mon inscription
                    </button>
                    <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Paiement sur HelloAsso</div>
                  </>
                )}

                {!showClaim ? (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setShowClaim(true)}
                    style={{ alignSelf: 'flex-start', padding: '4px 0' }}>
                    J'ai payé avec une autre adresse
                  </button>
                ) : (
                  <form onSubmit={handleClaimPayment} className="col gap-2">
                    <input type="email" required placeholder="adresse utilisée pour payer"
                      value={claimEmail} onChange={e => setClaimEmail(e.target.value)}
                      style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid var(--line)',
                        background: 'var(--ink-2)', color: 'var(--text)', fontSize: 13 }} />
                    <button type="submit" disabled={claimLoading} className="btn btn-ghost btn-sm">
                      {claimLoading ? 'Envoi…' : 'Envoyer'}
                    </button>
                    {claimMsg && <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{claimMsg}</div>}
                  </form>
                )}
              </div>
            )}

            {myTeam && ['weezevent', 'other'].includes(registrationMode(tournament)) && (
              <div className="card col gap-2" style={{ padding: 14, marginBottom: 14 }}>
                {tournament.externalUrl && (
                  <button className="btn btn-ghost btn-sm" style={{ width: '100%' }}
                    onClick={() => window.open(tournament.externalUrl, '_blank', 'noopener')}>
                    S'inscrire officiellement
                  </button>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                  <input type="checkbox" checked={myTeam.payment?.status === 'paid'}
                    onChange={e => handleDeclarePayment(e.target.checked)} />
                  Je me suis inscrit officiellement
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                  Déclaratif, non vérifié par Spike.
                </div>
              </div>
            )}

            {myFilteredTeam && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--sun-2)', marginBottom: 8, marginTop: 4 }}>Mon équipe</div>
                <TeamCard
                  team={myFilteredTeam}
                  isMyTeam={true}
                  user={user}
                  tournament={tournament}
                  onJoin={handleJoinTeam}
                  onRequestJoin={handleRequestJoin}
                  onLeave={handleLeaveTeam}
                  onDelete={handleDeleteTeam}
                  onEdit={openEditTeam}
                  onRemoveExternal={handleRemoveExternal}
                  actionLoading={actionLoading}
                  myTeam={myTeam}
                  isPast={isPast}
                  onResultsClick={() => setResultsModalTeam(myFilteredTeam)}
                  onNavigateProfile={(id) => navigate(`/profil/${id}`)}
                  onChatClick={() => handleOpenChat(myFilteredTeam)}
                />
              </>
            )}

            {otherTeams.length > 0 && (
              <>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.09em', color: 'var(--text-3)', marginBottom: 8, marginTop: myFilteredTeam ? 18 : 4 }}>Autres équipes inscrites · {otherTeams.length}</div>
                {otherTeams.map(team => (
                  <TeamCard
                    key={team.id}
                    team={team}
                    isMyTeam={false}
                    user={user}
                    tournament={tournament}
                    onJoin={handleJoinTeam}
                    onRequestJoin={handleRequestJoin}
                    onLeave={handleLeaveTeam}
                    onDelete={handleDeleteTeam}
                    onEdit={openEditTeam}
                    onRemoveExternal={handleRemoveExternal}
                    actionLoading={actionLoading}
                    myTeam={myTeam}
                    isPast={isPast}
                    onNavigateProfile={(id) => navigate(`/profil/${id}`)}
                  />
                ))}
              </>
            )}

            {filteredTeams.length === 0 && teams.length > 0 && (
              <div className="empty-state" style={{ padding: '30px 0' }}>
                <div className="empty-icon">🔍</div>
                <p className="empty-text">Aucune équipe correspondante</p>
              </div>
            )}

            {teams.length === 0 && (
              <div className="empty-state" style={{ padding: '30px 0' }}>
                <div className="empty-icon">🏐</div>
                <p className="empty-text">Aucune équipe pour le moment</p>
                <p style={{ fontSize: 12, color: '#B0C0D0', marginTop: 6 }}>Soyez le premier !</p>
              </div>
            )}
          </div>
        )}

        {/* ── MATCHS tab (à venir) ── */}
        {/* ── RÉSULTATS tab (l'onglet Matchs en direct est couvert par l'onglet Live) ── */}
        {activeTab === 'resultats' && (
          <div className="section col" style={{ marginTop: 18, gap: 12 }}>
            {isPast && myFilteredTeam ? (
              myFilteredTeam.results ? (
                <>
                  {/* Results filled */}
                  <div className="card col" style={{ gap: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Classement final</div>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
                          <span className="display" style={{ fontSize: 36 }}>{myFilteredTeam.results.placement || '—'}</span>
                          <span style={{ fontSize: 18, color: 'var(--text-3)', fontWeight: 600 }}>/ {teams.length}</span>
                        </div>
                      </div>
                      <button onClick={() => setResultsModalTeam(myFilteredTeam)}
                        style={{ padding: '7px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                          background: 'var(--ink-3)', color: 'var(--text-2)', border: 'none', cursor: 'pointer', marginTop: 4 }}>
                        Modifier
                      </button>
                    </div>
                    <div style={{ display: 'flex', gap: 0, borderTop: '1px solid var(--line)', paddingTop: 14 }}>
                      {[
                        { value: myFilteredTeam.results.wins, label: 'Victoires', color: 'var(--text)' },
                        { value: myFilteredTeam.results.losses, label: 'Défaites', color: '#E5503E' },
                        {
                          value: (myFilteredTeam.results.wins + myFilteredTeam.results.losses) > 0
                            ? Math.round(myFilteredTeam.results.wins / (myFilteredTeam.results.wins + myFilteredTeam.results.losses) * 100) + '%'
                            : '—',
                          label: 'Win rate', color: 'var(--sun)',
                        },
                      ].map((s, i) => (
                        <div key={i} style={{ flex: 1, textAlign: 'center', borderRight: i < 2 ? '1px solid var(--line)' : 'none' }}>
                          <div style={{ fontSize: 24, fontWeight: 800, color: s.color, fontFamily: "'Anton', sans-serif" }}>{s.value}</div>
                          <div className="mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2, textTransform: 'uppercase' }}>{s.label}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* TON RETOUR */}
                  <TonRetourSection />
                </>
              ) : (
                <div className="card col" style={{
                  padding: 24, alignItems: 'center', textAlign: 'center', gap: 10,
                  background: 'rgba(245,166,35,0.06)', borderColor: 'rgba(245,166,35,0.3)',
                }}>
                  <div style={{
                    width: 56, height: 56, borderRadius: 16, background: 'rgba(245,166,35,0.18)',
                    color: 'var(--sun-2)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M7 4h10v5a5 5 0 0 1-10 0V4z"/>
                      <path d="M7 6H4v2a3 3 0 0 0 3 3M17 6h3v2a3 3 0 0 1-3 3"/>
                      <path d="M9 17h6M10 21h4M12 14v3"/>
                    </svg>
                  </div>
                  <span className="display" style={{ fontSize: 22 }}>Tournoi terminé</span>
                  <span style={{ fontSize: 13, color: 'var(--text-2)', maxWidth: 260, lineHeight: 1.4 }}>
                    Ajoute tes scores pour conserver l'historique et ton classement.
                  </span>
                  <button onClick={() => setResultsModalTeam(myFilteredTeam)}
                    className="btn btn-primary" style={{ marginTop: 8, padding: '13px 22px' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
                    Mettre vos résultats
                  </button>
                </div>
              )
            ) : (
              <div className="card col" style={{
                padding: 24, alignItems: 'center', textAlign: 'center', gap: 10,
                background: 'rgba(245,166,35,0.06)', borderColor: 'rgba(245,166,35,0.3)',
              }}>
                <div style={{ fontSize: 40, marginBottom: 4 }}>🏆</div>
                <span className="display" style={{ fontSize: 22 }}>
                  {isPast ? 'Pas encore d\'équipe' : 'Bientôt disponible'}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.4 }}>
                  {isPast
                    ? 'Inscris-toi à ce tournoi pour saisir tes résultats.'
                    : 'Les résultats seront affichés ici une fois le tournoi terminé.'}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Sticky CTA removed per design ── */}

      {/* ── Create Team Full-screen Overlay ── */}
      {showCreateTeam && tournament && (() => {
        const categories = Array.isArray(tournament.categories) ? tournament.categories : null;
        const hasCategories = !!(categories && categories.length > 0);
        const selectedCategory = hasCategories
          ? (categories.find(c => c.id === newTeamCategoryId) || (categories.length === 1 ? categories[0] : null))
          : null;
        const fmt = selectedCategory?.format || newTeamFormat || (tournament.playerFormats || [])[0] || tournament.playerFormat || '4x4';
        const teamSize = parseInt(String(fmt).match(/^(\d+)/)?.[1] || '4');
        const usedSlots = 1 + pendingMembers.length + newTeamExternals.length;
        const freeSlots = Math.max(0, teamSize - usedSlots);
        const closeForm = () => { setShowCreateTeam(false); setEditingTeamId(null); setNewTeamName(''); setPendingMembers([]); setNewTeamExternals([]); setNewTeamVisibility('open'); setNewTeamFormat(''); setNewTeamGender(''); setNewTeamCategoryId(''); };
        const officialSpike = registrationMode(tournament) === 'spike';
        const catId = selectedCategory?.categoryId || selectedCategory?.id;
        const remainingSlots = officialSpike && selectedCategory?.maxTeams != null
          ? Math.max(0, selectedCategory.maxTeams - teams.filter(tm => (tm.categoryId || `${tm.playerFormat}_${tm.gender}`) === catId).length)
          : null;
        return (
          <>
            {officialSpike ? (
              <div className="card" style={{ padding: '10px 14px', marginBottom: 8,
                background: 'rgba(107,170,30,0.1)', border: '1px solid rgba(107,170,30,0.25)' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--lime)' }}>
                  Inscription officielle{remainingSlots != null ? ` · ${remainingSlots} place${remainingSlots > 1 ? 's' : ''} restante${remainingSlots > 1 ? 's' : ''}` : ''}
                </span>
              </div>
            ) : (
              <WarnOnce storageKey="warn_create_team" message={WARN_TEAM_MSG} />
            )}
            <div style={{
              position: 'fixed', top: 0, bottom: 0,
              left: '50%', transform: 'translateX(-50%)',
              width: 'min(430px, 100vw)',
              background: 'var(--ink)', zIndex: 200,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              {/* Header */}
              <div style={{ paddingTop: 46, paddingBottom: 12, paddingLeft: 20, paddingRight: 20,
                borderBottom: '1px solid var(--line)', flexShrink: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span className="display" style={{ fontSize: 18 }}>Nouvelle équipe</span>
                  <button type="button" onClick={closeForm}
                    style={{ color: 'var(--text-3)', fontSize: 13, fontWeight: 600, background: 'none', border: 'none', padding: '8px 0', cursor: 'pointer' }}>
                    Annuler
                  </button>
                </div>
              </div>

              {/* Scrollable content */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 120px' }}>
                {error && (
                  <div style={{ margin: '12px 0 0', padding: '10px 14px', borderRadius: 10,
                    background: 'rgba(229,80,62,0.1)', color: '#E5503E',
                    border: '1px solid rgba(229,80,62,0.25)', fontSize: 13, fontWeight: 600 }}>
                    {error}
                  </div>
                )}

                {/* Tournament info card */}
                <div className="card" style={{ marginTop: 16, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <div className="display" style={{ fontSize: 15, lineHeight: 1.1, textTransform: 'uppercase' }}>
                        {tournament.name}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 3 }}>
                        {tournament.location}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 700, background: 'var(--ink-3)', borderRadius: 6,
                      padding: '3px 8px', color: 'var(--text-2)', flexShrink: 0, alignSelf: 'flex-start' }}>
                      {fmt} {GENDER_LABELS[selectedCategory?.gender || newTeamGender || tournament.gender] || ''}
                    </div>
                  </div>
                  {hasCategories && selectedCategory && (
                    <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 8 }}>
                      {levelLabel(selectedCategory)}
                      {selectedCategory.meetingTime ? ` · RDV à ${formatTime(selectedCategory.meetingTime)}` : ''}
                      {selectedCategory.registrationCloseTime ? ` · Inscr. jusqu'à ${formatTime(selectedCategory.registrationCloseTime)}` : ''}
                      {selectedCategory.price ? ` · ${selectedCategory.price}€ / ${selectedCategory.priceMode === 'team' ? 'équipe' : 'joueur'}` : ''}
                      {selectedCategory.ageMax ? ` · Âge max ${selectedCategory.ageMax} ans` : ''}
                    </div>
                  )}
                </div>

                {/* Sélecteur de catégorie (format+genre+niveau) si le tournoi en définit plusieurs */}
                {hasCategories ? (
                  categories.length > 1 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                        fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>
                        Catégorie
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {categories.map(c => {
                          const on = (newTeamCategoryId || categories[0].id) === c.id;
                          return (
                            <button key={c.id} type="button" onClick={() => setNewTeamCategoryId(c.id)}
                              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                                padding: '10px 14px', borderRadius: 10, border: 'none', cursor: 'pointer', textAlign: 'left',
                                background: on ? 'var(--text)' : 'var(--ink-3)',
                                color: on ? 'var(--ink)' : 'var(--text-2)',
                                transition: 'background 0.15s' }}>
                              <span style={{ fontWeight: 700, fontSize: 13 }}>{c.format} · {GENDER_LABELS[c.gender] || c.gender} · {levelLabel(c)}</span>
                              <span style={{ fontSize: 11, opacity: 0.8, flexShrink: 0 }}>
                                {c.meetingTime ? formatTime(c.meetingTime) : ''}{c.price ? ` · ${c.price}€/${c.priceMode === 'team' ? 'équipe' : 'joueur'}` : ''}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )
                ) : (() => {
                  const fmts = tournament.playerFormats?.length ? tournament.playerFormats : (tournament.playerFormat ? [tournament.playerFormat] : []);
                  const gens = tournament.genders?.length ? tournament.genders : (tournament.gender ? [tournament.gender] : []);
                  const combos = fmts.flatMap(f => gens.map(g => ({ f, g, label: `${f} ${GENDER_LABELS[g] || g}` })));
                  if (combos.length <= 1) return null; // combo unique = présélectionné via handleCreateTeam
                  const selKey = `${newTeamFormat}|${newTeamGender}`;
                  return (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                        fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>
                        Catégorie
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {combos.map(({ f, g, label }) => {
                          const key = `${f}|${g}`;
                          const on = selKey === key;
                          return (
                            <button key={key} type="button"
                              onClick={() => { setNewTeamFormat(f); setNewTeamGender(g); }}
                              style={{ padding: '9px 16px', borderRadius: 8, fontSize: 13, fontWeight: 700, border: 'none', cursor: 'pointer',
                                background: on ? 'var(--text)' : 'var(--ink-3)',
                                color: on ? 'var(--ink)' : 'var(--text-2)',
                                transition: 'background 0.15s' }}>
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                {/* Team name */}
                <div style={{ marginTop: 16 }}>
                  <div className="card" style={{ padding: '10px 14px' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                      fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 6 }}>
                      Nom de l'Équipe
                    </div>
                    <input type="text" value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
                      placeholder="ex: Les Volcans" autoFocus
                      style={{ width: '100%', background: 'transparent', border: 0, outline: 'none',
                        color: 'var(--text)', fontSize: 15, fontWeight: 600, fontFamily: 'inherit' }} />
                  </div>
                </div>

                {/* Player slots */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                    fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>
                    Joueurs · {usedSlots}/{teamSize}
                  </div>
                  {/* Captain */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                    background: 'var(--ink-2)', borderRadius: 12, marginBottom: 8, border: '1px solid var(--line)' }}>
                    <div className={`av-circle av-sm ${!user.avatarUrl ? avatarColor(user.id) : ''}`} style={{ flexShrink: 0 }}>
                      {user.avatarUrl
                        ? <img src={user.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : initials(user.firstName, user.lastName)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>{user.firstName} {(user.lastName || '').charAt(0)}.</span>
                      <span style={{ fontSize: 11, color: 'var(--sun-2)', fontWeight: 700, marginLeft: 6 }}>★ capitaine</span>
                    </div>
                  </div>
                  {/* Pending members */}
                  {pendingMembers.map(m => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      background: 'var(--ink-2)', borderRadius: 12, marginBottom: 8, border: '1px solid var(--line)' }}>
                      <div className={`av-circle av-sm ${!m.avatarUrl ? avatarColor(m.id) : ''}`} style={{ flexShrink: 0 }}>
                        {m.avatarUrl
                          ? <img src={m.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : initials(m.firstName, m.lastName)}
                      </div>
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 14, fontWeight: 700 }}>{m.firstName} {(m.lastName || '').charAt(0)}.</span>
                      </div>
                      <button type="button" onClick={() => setPendingMembers(prev => prev.filter(x => x.id !== m.id))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16, padding: '0 4px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  {/* External members — inline editable slots */}
                  {newTeamExternals.map((ext, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      background: 'var(--ink-2)', borderRadius: 12, marginBottom: 8, border: '1px solid var(--line)' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--ink-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: 'var(--text-3)' }}>EXT</div>
                      <input type="text" value={ext.name} placeholder="Prénom Nom"
                        onChange={e => setNewTeamExternals(prev => prev.map((x, j) => j === i ? { name: e.target.value } : x))}
                        style={{ flex: 1, background: 'transparent', border: 0, outline: 'none',
                          color: 'var(--text)', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' }} />
                      <button type="button" onClick={() => setNewTeamExternals(prev => prev.filter((_, j) => j !== i))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: 16, padding: '0 4px', lineHeight: 1 }}>✕</button>
                    </div>
                  ))}
                  {/* Place libre slots */}
                  {Array.from({ length: freeSlots }).map((_, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                      borderRadius: 12, marginBottom: 8, border: '1.5px dashed var(--line-2)' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: 'var(--ink-3)', border: '1.5px dashed var(--line-2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-3)" strokeWidth="2">
                          <circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/>
                        </svg>
                      </div>
                      <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                        fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                        Place libre
                      </div>
                    </div>
                  ))}
                  {/* Add player search */}
                  {freeSlots > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <PlayerSearch
                        token={token}
                        excludeIds={[user.id, ...pendingMembers.map(m => m.id)]}
                        onSelect={u => setPendingMembers(prev => prev.length < teamSize - 1 ? [...prev, u] : prev)}
                        placeholder="Ajouter un joueur Spike…"
                      />
                    </div>
                  )}

                  {/* Suggestions from group members */}
                  {freeSlots > 0 && groupSuggestions.filter(s => !pendingMembers.some(m => m.id === s.id)).length > 0 && (
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                          fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)' }}>
                          Suggestions
                        </div>
                        {freeSlots > 0 && (
                          <button type="button" className="button-secondary btn-sm"
                            onClick={() => setNewTeamExternals(prev => [...prev, { name: '' }])}
                            style={{ fontSize: 11, padding: '4px 10px', fontWeight: 600 }}>
                            Ajouter un joueur externe
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {groupSuggestions
                          .filter(s => !pendingMembers.some(m => m.id === s.id))
                          .map(s => (
                            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                              padding: '8px 12px', borderRadius: 12, background: 'var(--ink-2)', border: '1px solid var(--line)' }}>
                              <div className={`av-circle av-sm ${!s.avatarUrl ? avatarColor(s.id) : ''}`} style={{ flexShrink: 0, fontSize: 11 }}>
                                {s.avatarUrl
                                  ? <img src={s.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  : initials(s.firstName, s.lastName)}
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>
                                  {s.firstName} {s.lastName}
                                </div>
                                {s.level && (
                                  <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                                    color: 'var(--text-3)', marginTop: 1 }}>
                                    {LEVEL_LABELS[s.level] || s.level}
                                  </div>
                                )}
                              </div>
                              <button type="button"
                                onClick={() => setPendingMembers(prev => prev.length < teamSize - 1 ? [...prev, s] : prev)}
                                style={{ width: 28, height: 28, borderRadius: '50%', border: 'none', cursor: 'pointer',
                                  background: 'var(--sun)', color: '#fff',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
                                  <path d="M12 5v14M5 12h14"/>
                                </svg>
                              </button>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Visibility — masqué quand équipe complète */}
                {freeSlots > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", fontSize: 10,
                      fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>
                      Qui peut rejoindre ?
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[['open', 'Ouvert à tous'], ['group_only', 'Mes groupes uniquement']].map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setNewTeamVisibility(k)} style={{
                          flex: 1, padding: '9px 0', borderRadius: 8, fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer',
                          background: newTeamVisibility === k ? 'var(--text)' : 'var(--ink-3)',
                          color: newTeamVisibility === k ? 'var(--ink)' : 'var(--text-2)',
                          transition: 'background 0.15s',
                        }}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Fixed CTA */}
              <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0,
                padding: '12px 20px 28px',
                background: 'linear-gradient(to top, var(--ink) 70%, transparent)' }}>
                <button type="button"
                  onClick={editingTeamId ? handleUpdateTeam : handleCreateTeam}
                  disabled={actionLoading}
                  className="btn btn-primary" style={{ width: '100%', padding: '15px 20px' }}>
                  {actionLoading ? (editingTeamId ? 'Enregistrement…' : 'Création…') : (editingTeamId ? "Modifier l'équipe" : "Créer l'équipe")}
                </button>
              </div>
            </div>
          </>
        );
      })()}

      {/* ── Modals ── */}
      {resultsModalTeam && tournament && (
        <ResultsModal
          tournament={tournament}
          team={resultsModalTeam}
          token={token}
          onClose={(saved) => { setResultsModalTeam(null); if (saved) fetchData(); }}
        />
      )}

    </div>
  );
}

function PlayerSearch({ token, onSelect, excludeIds = [], placeholder }) {
  const [q, setQ]           = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  const search = (val) => {
    setQ(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/users/search?q=${encodeURIComponent(val.trim())}`,
          { headers: { Authorization: `Bearer ${token}` } });
        setResults(res.data.filter(u => !excludeIds.includes(u.id)));
      } catch { setResults([]); } finally { setLoading(false); }
    }, 300);
  };

  const handleSelect = (u) => { onSelect(u); setQ(''); setResults([]); };

  return (
    <div>
      <div className="player-search-wrap">
        <span style={{ fontSize:14, marginLeft:2, color:'#90A0B0' }}>🔍</span>
        <input value={q} onChange={e => search(e.target.value)}
          placeholder={placeholder || 'Prénom ou nom…'}
          style={{ flex:1, border:'none', outline:'none', fontSize:13, background:'transparent' }} />
        {q && <button className="search-clear" style={{ position:'static' }}
          onClick={() => { setQ(''); setResults([]); }}>✕</button>}
      </div>
      {loading && <div style={{ fontSize:12, color:'#90A0B0', padding:'4px 0' }}>Recherche…</div>}
      {results.length > 0 && (
        <div className="player-search-results">
          {results.map(u => (
            <div key={u.id} className="player-search-item" onClick={() => handleSelect(u)}>
              <div className={`av-circle av-sm ${!u.avatarUrl ? avatarColor(u.id) : ''}`} style={{ flexShrink:0 }}>
                {u.avatarUrl
                  ? <img src={u.avatarUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : initials(u.firstName, u.lastName)}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ fontSize:13, fontWeight:600, color:'#334' }}>
                  {u.firstName} {u.lastName}
                  {u.isGroupMember && <span style={{ fontSize:10, color:'var(--primary)', fontWeight:700, marginLeft:4 }}>· Groupe</span>}
                </div>
                {u.level && <div style={{ fontSize:11, color:'#90A0B0' }}>{u.level}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function TeamCard({ team, isMyTeam, user, tournament, onJoin, onRequestJoin, onLeave, onDelete, onEdit,
  onRemoveExternal, actionLoading, myTeam, isPast, onResultsClick, onNavigateProfile, onChatClick }) {

  const [showJoinForm, setShowJoinForm] = useState(false);

  const isCreator     = team.creator === user.id;
  const externals     = team.externalMembers || [];
  const totalOccupied = team.members.length + externals.length;
  const isFull        = totalOccupied >= team.maxSize;
  const spotsLeft   = team.maxSize - totalOccupied;
  const blockReason = !isMyTeam && !myTeam ? getJoinBlockReason(user, tournament, team) : null;
  const canJoin     = !myTeam && !blockReason && !isPast;
  const hasRequested = !isMyTeam && (team.joinRequests || []).some(r => r.userId === user.id);

  const cancelJoin = () => { setShowJoinForm(false); };

  return (
    <div className={`team-card ${isMyTeam ? 'team-card-mine' : ''}`}>
      <div className="team-header">
        <div>
          <h3 style={{ marginBottom: 2 }}>{team.name}</h3>
          {(team.playerFormat || team.averageLevelLabel) && (
            <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600, marginTop: 2 }}>
              {formatCategories(team).join(' · ')}{team.playerFormat && team.averageLevelLabel ? ' · ' : ''}{team.averageLevelLabel ? `Niveau moy : ${team.averageLevelLabel}` : ''}
            </div>
          )}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {team.visibility === 'group_only' && (
            <span className="vis-badge">👥 Groupes</span>
          )}
          <span className={`team-spots ${isFull ? 'full' : ''}`}>
            {isFull ? 'Complet' : `${spotsLeft} place${spotsLeft > 1 ? 's' : ''} restante${spotsLeft > 1 ? 's' : ''}`}
          </span>
        </div>
      </div>

      {/* Members */}
      <div style={{ display:'flex', flexDirection:'column', gap:'5px', marginBottom:'8px' }}>
        {/* Real members */}
        {team.memberDetails.map(member => (
          <div key={member.id} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <div className={`av-circle av-sm ${!member.avatarUrl ? avatarColor(member.id) : ''}`}
              style={{ cursor:'pointer' }}
              onClick={() => onNavigateProfile?.(member.id)}>
              {member.avatarUrl
                ? <img src={member.avatarUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                : initials(member.firstName, member.lastName)
              }
            </div>
            <span style={{ fontSize:12, color:'#445' }}>
              {member.firstName} {member.lastName}{member.id === user.id ? ' (moi)' : ''}
            </span>
          </div>
        ))}

        {/* External members */}
        {externals.map(ext => (
          <div key={ext.id} style={{ display:'flex', alignItems:'center', gap:'6px' }}>
            <div className="av-circle av-sm" style={{ background:'#E8EEF8', border:'1.5px dashed #AAB8CC', fontSize:14, flexShrink:0 }}>👤</div>
            <span style={{ fontSize:12, color:'#90A0B0', fontStyle:'italic', flex:1 }}>
              {ext.name || 'Joueur externe'}
              {ext.reservedBy === user.id ? <span style={{ color:'#B0B8C8', fontSize:11 }}> (réservé par vous)</span> : ''}
            </span>
            {(ext.reservedBy === user.id || isCreator) && (
              <button className="button-danger btn-sm" disabled={actionLoading}
                style={{ padding:'2px 6px', fontSize:11, flexShrink:0 }}
                onClick={() => onRemoveExternal(team.id, ext.id)}>✕</button>
            )}
          </div>
        ))}

        {/* Empty spots */}
        {Array.from({ length: spotsLeft }).map((_, i) => (
          <div key={`e${i}`} className="member-chip empty">Place libre</div>
        ))}
      </div>


      {/* Actions */}
      <div className="team-actions" style={{ justifyContent:'flex-end' }}>
        {isMyTeam ? (
          <>
            {isPast && !team.results && (
              <button className="button-secondary btn-sm" onClick={onResultsClick}>
                📊 Résultats
              </button>
            )}
            {isMyTeam && (
              <button className="button-secondary btn-sm" onClick={onChatClick}
                style={{ display:'flex', alignItems:'center', gap:5 }}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
                Chat d'équipe
              </button>
            )}
            <button className="button-secondary btn-sm" onClick={() => onEdit(team)}>Modifier</button>
            {isCreator
              ? <button className="button-danger btn-sm" disabled={actionLoading} onClick={() => onDelete(team)}>Supprimer</button>
              : <button className="button-danger btn-sm" disabled={actionLoading} onClick={() => onLeave(team.id)}>Quitter</button>
            }
          </>
        ) : hasRequested ? (
          <button disabled style={{ opacity:0.6, cursor:'default', background:'#EEF2FB', color:'var(--primary)', border:'1px solid var(--border)', borderRadius:10, padding:'7px 14px', fontSize:13, fontWeight:700 }}>
            ✓ Demande envoyée
          </button>
        ) : canJoin ? (
          showJoinForm ? (
            <div style={{ width:'100%' }}>
              <p style={{ fontSize:12, color:'#445', fontWeight:600, marginBottom:8 }}>
                Envoyer une demande pour <strong>{team.name}</strong>
              </p>
              <div style={{ display:'flex', gap:8, marginTop:8 }}>
                <button disabled={actionLoading} onClick={() => { onRequestJoin(team.id); setShowJoinForm(false); }}>
                  Envoyer la demande
                </button>
                <button className="button-secondary" onClick={cancelJoin}>Annuler</button>
              </div>
            </div>
          ) : (
            <button disabled={actionLoading} onClick={() => setShowJoinForm(true)}>Rejoindre</button>
          )
        ) : !myTeam && blockReason ? (
          <p className="join-blocked-reason">{blockReason}</p>
        ) : null}
      </div>
    </div>
  );
}

export default TournamentDetail;
